import { useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient, type Query, type QueryKey } from "@tanstack/react-query";
import { createRevisionBus, createKeyedRevisionRefresh, type RevisionEvent, type RealtimeStatus } from "@a2z/realtime";
import { subscribeAuthenticatedRevisions } from "@a2z/realtime/firebase";
import { getRestaurantFirebaseAuth, getRestaurantFirestore } from "@/lib/firebase/config";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";

import { watchOnlineOrderExpiry } from "./onlineOrderExpiry";

export type RestaurantRealtimeEvent = RevisionEvent & { storeId: string };
const events = createRevisionBus<RestaurantRealtimeEvent>();
export const subscribeRestaurantRealtime = events.subscribe;

const resourcesByTopic: Record<string, ReadonlySet<string>> = {
  floor: new Set(["grid", "off-premise-open", "service-requests", "invoice", "invoices", "areas", "tables", "dispatch"]),
  kitchen: new Set(["kitchen-board"]),
  shifts: new Set(["shift-current", "shift-summary", "shift-drawers", "shift-recountable", "shifts-mine", "shifts", "day-close"]),
  onlineOrders: new Set(["online-orders", "off-premise-open"]),
  restaurantSettings: new Set(["settings", "aggregators"]),
  // P6b: the platforms' period totals are Sales & costs' money per platform,
  // so they refresh on the same signal (review of #310).
  reports: new Set(["reports", "day-close", "aggregator-totals"]),
  feedback: new Set(["feedback"]),
  menu: new Set(["menu-items", "kitchen-availability", "kitchen-stations", "addon-groups", "ingredients", "price-tiers", "pos-price-tiers", "kitchen-board"]),
};
const accessResources = new Set(["roles", "members", "invites", "staff", "team"]);
export const restaurantRevisionTopics = [...Object.keys(resourcesByTopic), "printing", "access"];

export function restaurantRevisionAffects(key: QueryKey, topics: readonly string[]): boolean {
  const resource = String(key[1]);
  if (topics.includes("access") && (key[0] === "session" || accessResources.has(resource))) return true;
  if (key[0] === "restaurant" && topics.some(topic => resourcesByTopic[topic]?.has(resource))) return true;
  if (topics.includes("menu") && key[0] === "seller" && ["categories", "product", "theme", "themes"].includes(resource)) return true;
  return (topics.includes("restaurantSettings") || topics.includes("menu")) && key[0] === "payment-options";
}

/** One authenticated listener for the restaurant, independent of bell history. */
export function useRestaurantRealtime(sellerId?: string, storeId?: string) {
  const client = useQueryClient();
  const [status, setStatus] = useState<RealtimeStatus>("connecting");
  const lifetime = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  useEffect(() => {
    if (!sellerId || !storeId || lifetime.changing) return;
    const generation = lifetime.generation;
    return watchOnlineOrderExpiry(client, () => !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation);
  }, [client, lifetime.changing, lifetime.generation, sellerId, storeId]);
  useEffect(() => {
    if (!sellerId || !storeId || lifetime.changing) return;
    const auth = getRestaurantFirebaseAuth();
    const db = getRestaurantFirestore();
    let active = true;
    const generation = lifetime.generation;
    const isCurrent = () => active && !getPrivateSessionState().changing &&
      getPrivateSessionState().generation === generation;
    const refresh = createKeyedRevisionRefresh<Query>({ isCurrent, refresh: async (query) => {
      // Await only this key's earlier snapshot, never an unrelated slow read.
      if (query.state.fetchStatus === "fetching") await query.promise?.catch(() => undefined);
      if (isCurrent() && query.isActive() && client.getQueryCache().get(query.queryHash) === query) {
        await client.refetchQueries({ queryKey: query.queryKey, exact: true, type: "active" }, { cancelRefetch: false });
      }
    } });
    const onChange = (event: RevisionEvent) => {
      if (!isCurrent()) return;
      events.emit({ ...event, storeId });
      const predicate = (query: { queryKey: QueryKey }) => restaurantRevisionAffects(query.queryKey, event.topics);
      // Inactive entries become stale without issuing requests.
      void client.invalidateQueries({ predicate, refetchType: "none" });
      client.getQueryCache().findAll({ predicate }).forEach((query) => {
        if (query.isActive()) refresh.enqueue(query, event);
      });
    };
    if (!auth || !db) {
      queueMicrotask(() => { if (isCurrent()) setStatus("unconfigured"); });
      if (import.meta.env.DEV) console.warn("[restaurant-realtime] Firebase is not configured; live updates are unavailable.");
      // No listener exists to catch up on retry. Refresh only currently enabled
      // operational queries when the user explicitly asks; never on a timer.
      const retry = () => onChange({ topics: restaurantRevisionTopics, catchUp: true });
      window.addEventListener("a2z:realtime-retry", retry);
      return () => { active = false; refresh.dispose(); window.removeEventListener("a2z:realtime-retry", retry); };
    }
    const stop = subscribeAuthenticatedRevisions({
      auth, db, ownerId: sellerId, ownerType: "SELLER", storeId, isCurrent,
      sources: [
        { path: `realtimeStores/${storeId}`, topics: restaurantRevisionTopics },
        { path: `realtimeOwners/${sellerId}`, topics: ["access"] },
      ],
      onStatus: setStatus,
      onChange,
    });
    return () => { active = false; refresh.dispose(); stop(); };
  }, [client, lifetime.changing, lifetime.generation, sellerId, storeId]);
  return status;
}
